import React, {
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  TableCell,
  TextField
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { withStyles } from '@mui/styles';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { useForm } from 'react-hook-form';
import { compareObjects } from 'src/utils/validate';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { AppActions } from 'src/store/AppReducer';
import { getTimeEntrySummaryReport } from 'src/services/timeManagementService';
import NoRecord from '../common/NoRecord';
import moment from 'moment';
import { useAppSelector } from 'src/hooks/hooks';
import fileDownload from 'js-file-download';
import ClientDropdown from '../common/ClientDropdown';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SingleSelect from 'src/content/common/SingleSelect';
import { TTimeEntryReportSummery } from '../../types/time-entry-report.type';
import { ITimeEntrySummaryReportFilter } from '../../interface/time-entry-report.interface';
import ExcelJS from 'exceljs';
import ProjectDropdownFilter from '../common/ProjectDropdownFilter';
import ResourceDropdownFilter from '../common/ResourceDropdownFilter';

interface SummarizedReportTableProps {
  permissions: any;
}

export interface SummarizedReportTableRef {
  handleDownload: () => void;
}

const SummarizedReportTable = forwardRef<
  SummarizedReportTableRef,
  SummarizedReportTableProps
>(({ permissions }, ref) => {
  const pagination_wrapper_ref = useRef(null);
  const appUser = useSelector((state: RootStateType) => state.appReducer?.user);
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const dispatch = useDispatch();
  const myRef = useRef<DateRangePicker>();

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [filterPayload, setFilterPayload] = useState({
    clientId: null,
    projectId: null,
    employeeId: null,
    datePeriod: null,
    summaryBy: null
  });

  let initialFilter = {
    clientId: null,
    projectId: null,
    employeeId: null,
    datePeriod: {
      fromDate: moment(new Date()).format('YYYY-MM-DD'),
      toDate: moment(new Date()).format('YYYY-MM-DD')
    },
    summaryBy: { value: 'DAY', name: 'Day' },
    isSubmitted: true,
    limit: 10,
    offset: 0
  } as unknown as ITimeEntrySummaryReportFilter;

  const [filter, setFilter] = useState(
    initialFilter as ITimeEntrySummaryReportFilter
  );
  const [prevFilter, setPrevFilter] = useState(
    initialFilter as ITimeEntrySummaryReportFilter
  );

  const [toggleFilter, setToggleFilter] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(true);
  const [isClearDisabled, setIsClearDisabled] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const summarizedByList = [
    {
      value: 'DAY',
      name: 'Day'
    },
    {
      value: 'WEEK',
      name: 'Week'
    },
    {
      value: 'MONTH',
      name: 'Month'
    },
    {
      value: 'QUARTER',
      name: 'Quarter'
    },
    {
      value: 'AGGREGATED',
      name: 'Aggregated'
    }
  ];

  const [data, setData] = useState<TTimeEntryReportSummery[]>([]);

  const [total, setTotal] = useState<number>(0);

  const { register, handleSubmit, reset, setValue } =
    useForm<ITimeEntrySummaryReportFilter>({
      defaultValues: initialFilter
    });

  const handlePageChange = (event: any, newPage: number): void => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      offset: newPage,
      limit: filter.limit,
      isSubmitted: true
    }));
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setFilter({
      ...prevFilter,
      limit: parseInt(event.target.value),
      isSubmitted: true
    });
  };

  const onSubmit = () => {
    const newFilter = {
      ...filter,
      isSubmitted: true,
      offset: 0,
      limit: 10
    };

    if (isFilterChanged) {
      setFilter(newFilter);
      setPrevFilter(newFilter);
      setIsFilterChanged(false);
    }
  };

  //clear the filter
  const resetFilterResult = () => {
    reset();

    //clear date picker
    const today = new Date();
    myRef.current?.setStartDate(today);
    myRef.current?.setEndDate(today);

    const result = compareObjects(initialFilter, filter, 'isSubmitted');
    !result &&
      setFilter({
        ...initialFilter,
        isSubmitted: true
      });

    setPrevFilter({
      ...initialFilter,
      isSubmitted: true
    });
  };

  useEffect(() => {
    const newFilter = {
      clientId: filter.clientId,
      datePeriod: {
        fromDate: filter.datePeriod.fromDate,
        toDate: filter.datePeriod.toDate
      },
      projectId: filter.projectId,
      employeeId: filter.employeeId,
      summaryBy: filter.summaryBy
    };

    const isNullish = Object.values(newFilter).every((value) => {
      if (value === null) {
        return true;
      }

      return false;
    });

    if (Object.keys(newFilter).length === 0 || isNullish) {
      setIsDisabled(true);
      setIsClearDisabled(true);
    } else {
      setIsDisabled(false);
      setIsClearDisabled(false);
    }
  }, [filter]);

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3rem'
    }
  })(TableRow);

  const getTimeEntryReportSummary = () => {
    dispatch(AppActions.showLoader());

    const payload = {
      clientId: filter.clientId,
      projectId: filter.projectId,
      employeeId: filter.employeeId,
      datePeriod: filter.datePeriod,
      summaryBy: filter?.summaryBy?.value
    };
    setFilterPayload(payload);
    getTimeEntrySummaryReport(payload, filter.offset, filter.limit)
      .then((res) => {
        const data = res?.data?.data;
        let transformedData = tranceFormData(
          data?.timeEntrySummaryReportDtoList
        );

        // order by base on MONTH
        if (filter?.summaryBy?.value === 'MONTH') {
          transformedData.sort((a, b) => {
            const monthA = monthNames.indexOf(a.period);
            const monthB = monthNames.indexOf(b.period);
            return monthA - monthB;
          });
        }

        setData(transformedData);
        setTotal(data?.totalCount);
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
      });
  };

  const tranceFormData = (response: TTimeEntryReportSummery[]) => {
    if (response?.length > 0) {
      const resData = response.map((item: TTimeEntryReportSummery) => {
        return {
          ...item,
          period: handleSummaryText(item.period),
          totalHours: formatTime(item.totalHours)
        };
      });
      return resData;
    }
    return [];
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString
      .replace('h', '')
      .replace('m', '')
      .trim()
      .split(' ');

    const formattedHours = hours.padStart(2, '0');
    const formattedMinutes = minutes.padStart(2, '0');

    return `${formattedHours} : ${formattedMinutes}`;
  };

  //call API for load first time and when click filter button
  useEffect(() => {
    if (filter.isSubmitted) {
      getTimeEntryReportSummary();
      setFilter((prev) => ({
        ...prev,
        isSubmitted: false
      }));
    }
  }, [filter]);

  const handleDownload = () => {
    getTimeEntrySummaryReport(filterPayload)
      .then((res) => {
        const data = res?.data?.data;
        let transformedData = tranceFormData(
          data?.timeEntrySummaryReportDtoList
        );

        // order by base on MONTH
        if (filter?.summaryBy?.value === 'MONTH') {
          transformedData.sort((a, b) => {
            const monthA = monthNames.indexOf(a.period);
            const monthB = monthNames.indexOf(b.period);
            return monthA - monthB;
          });
        }
        if (transformedData?.length > 0) {
          downloadExcelSheet(transformedData);
        } else {
          setSnackBarMessage('No data in selected date range.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        }
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
      });
  };

  const downloadExcelSheet = (downloadData: TTimeEntryReportSummery[]) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Summary');

    worksheet.columns = [
      { header: 'Client Name', key: 'clientName', width: 15 },
      { header: 'Project Name', key: 'projectName', width: 15 },
      { header: 'Resource Name', key: 'employeeName', width: 15 },
      { header: 'Summary By', key: 'summaryBy', width: 15 },
      { header: 'Duration (hh:mm)', key: 'totalNumberOfHours', width: 15 }
    ];

    worksheet.getRow(1).eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '3498DB' }
      };
      cell.font = {
        color: { argb: 'FFFFFF' },
        bold: true
      };
    });

    downloadData.forEach((item) => {
      worksheet.addRow({
        clientName: item.clientName,
        projectName: item.projectName,
        employeeName: item.employeeName,
        summaryBy: item.period,
        totalNumberOfHours: item.totalHours
      });
    });

    const currentDatetime = moment().format('YYYYMMDDHHmm');

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `TimeEntry_Summarized_Report_${currentDatetime}.xlsx`;
      link.click();
    });
  };

  useImperativeHandle(ref, () => ({
    handleDownload
  }));

  const handleSummaryText = (summaryText: string) => {
    if (filter?.summaryBy?.value === 'MONTH') {
      const monthNumber = parseInt(summaryText.replace('Month ', ''), 10);
      return getMonthShortName(monthNumber);
    } else if (filter?.summaryBy?.value === 'QUARTER') {
      const quarterNumber = parseInt(summaryText.replace('Quarter ', ''), 10);
      return convertQuarterText(quarterNumber);
    } else {
      return summaryText;
    }
  };

  const convertQuarterText = (quarter: number) => {
    let quarterText;

    if (quarter === 1) {
      quarterText = '1st Quarter';
    } else if (quarter === 2) {
      quarterText = '2nd Quarter';
    } else if (quarter === 3) {
      quarterText = '3rd Quarter';
    } else {
      quarterText = '4th Quarter';
    }
    return quarterText;
  };

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  const getMonthShortName = (monthNumber: number) => {
    return monthNames[monthNumber - 1];
  };

  // custom table cloumn cell
  const CustomTableCloumnCell = ({ content }) => {
    return (
      <TableCell
        style={{
          background: 'white',
          whiteSpace: 'nowrap',
          alignItems: 'center',
          color: '#232C68',
          fontSize: '11px'
        }}
      >
        <Typography
          paddingBottom="0.25rem"
          paddingTop="0.5rem"
          variant="body2"
          style={{ fontSize: '11px' }}
          gutterBottom
          noWrap
        >
          {content}
        </Typography>
      </TableCell>
    );
  };

  const getInitSettings = () => {
    const today = new Date();
    let settings = {
      startDate: today,
      endDate: today
    };
    return settings;
  };

  return (
    <div className="time-sheet-report-table">
      <Card>
        <Grid
          sx={{ px: 2, py: 2 }}
          item
          xs={12}
          display="flex"
          justifyContent="space-between"
          spacing={1}
        >
          <Button
            id="btn-setToggleFilter"
            sx={{
              ml: 1,
              mt: { xs: 2, md: 0 },
              color: `${!toggleFilter && 'gray'}`
            }}
            variant="outlined"
            startIcon={<FilterListIcon style={{ fontSize: '15px' }} />}
            onClick={() => {
              setToggleFilter(!toggleFilter);
            }}
            style={{ height: '1.75rem', fontSize: '12px' }}
          >
            Filters
          </Button>
          <Grid style={{ fontSize: '12px' }}>
            Data display is for the duration :{' '}
            {moment(filter?.datePeriod?.fromDate).format('YYYY MMM DD') +
              ' - ' +
              moment(filter?.datePeriod?.toDate).format('YYYY MMM DD')}
          </Grid>
          <Grid item xs={3} sx={{ paddingLeft: '7px' }}>
            <SingleSelect
              {...register('summaryBy')}
              title="Summarized By"
              dropdownList={summarizedByList}
              value={filter.summaryBy || ''}
              onChange={(e: any) => {
                setFilter((prev) => ({
                  ...prev,
                  summaryBy: e.target.value,
                  isSubmitted: true,
                  offset: 0,
                  limit: 10
                }));
                setValue('summaryBy', e.target.value);
              }}
            />
          </Grid>
        </Grid>
        {toggleFilter && (
          <Grid px={2} pb={2} style={{ margin: 0 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                spacing={1}
                style={{
                  justifyContent: 'between',
                  flexWrap: 'nowrap',
                  alignItems: 'center'
                }}
              >
                <Grid item xs={3} width="20%">
                  <ClientDropdown
                    title="Client"
                    {...register('clientId')}
                    value={filter?.clientId || null}
                    onChange={(selected: string) => {
                      setFilter((prev) => ({
                        ...prev,
                        clientId: selected != '' ? selected : null
                      }));
                      setValue('clientId', selected);
                      setIsFilterChanged(true);
                    }}
                  />
                </Grid>
                <Grid item xs={3} width="20%">
                  <ProjectDropdownFilter
                    title="Project"
                    value={filter?.projectId || ''}
                    customerIds={
                      filter?.clientId ? filter.clientId.split(',') : []
                    }
                    onChange={(selected) => {
                      const selectedArray = selected.split(',').filter(Boolean);
                      setFilter((prev) => ({
                        ...prev,
                        projectId: selectedArray.length
                          ? selectedArray.join(',')
                          : null
                      }));
                      setValue('projectId', selected);
                      setIsFilterChanged(true);
                    }}
                  />
                </Grid>
                <Grid item xs={3} width="20%">
                  <ResourceDropdownFilter
                    title="Resource Name"
                    {...register('employeeId')}
                    value={filter?.employeeId || ''}
                    projectIds={
                      filter?.projectId ? filter.projectId.split(',') : []
                    }
                    customerIds={
                      filter?.clientId ? filter.clientId.split(',') : []
                    }
                    startDate={
                      filter?.datePeriod?.fromDate
                        ? moment(filter.datePeriod.fromDate).format(
                            'YYYY-MM-DD'
                          )
                        : null
                    }
                    endDate={
                      filter?.datePeriod?.toDate
                        ? moment(filter.datePeriod.toDate).format('YYYY-MM-DD')
                        : null
                    }
                    onChange={(selected: string) => {
                      setFilter((prev) => ({
                        ...prev,
                        employeeId: selected
                      }));
                      setValue('employeeId', selected);
                      setIsFilterChanged(true);
                    }}
                  />
                </Grid>
                <Grid item xs={3} width="20%">
                  <div className="date-range-picker">
                    <DateRangePicker
                      ref={myRef}
                      initialSettings={getInitSettings()}
                      onApply={(event, picker) => {
                        setFilter((prev) => ({
                          ...prev,
                          datePeriod: {
                            fromDate: moment(picker.startDate).format(
                              'YYYY-MM-DD'
                            ),
                            toDate: moment(picker.endDate).format('YYYY-MM-DD')
                          }
                        }));
                        myRef.current?.setStartDate(picker.startDate);
                        myRef.current?.setEndDate(picker.endDate);

                        setValue(
                          'datePeriod.fromDate',
                          moment(picker.startDate).format('YYYY-MM-DD')
                        );
                        setValue(
                          'datePeriod.toDate',
                          moment(picker.endDate).format('YYYY-MM-DD')
                        );
                        setIsFilterChanged(true);
                      }}
                      onCancel={() => {
                        const today = new Date();
                        myRef.current?.setStartDate(today);
                        myRef.current?.setEndDate(today);

                        setFilter((prev) => ({
                          ...prev,
                          datePeriod: {
                            fromDate: moment(today).format('YYYY-MM-DD'),
                            toDate: moment(today).format('YYYY-MM-DD')
                          }
                        }));
                        setValue(
                          'datePeriod.fromDate',
                          moment(today).format('YYYY-MM-DD')
                        );
                        setValue(
                          'datePeriod.toDate',
                          moment(today).format('YYYY-MM-DD')
                        );
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <TextField
                          placeholder="YYYY-MM-DD - YYYY-MM-DD"
                          value={
                            filter?.datePeriod?.fromDate +
                            ' - ' +
                            filter?.datePeriod?.toDate
                          }
                          id="date"
                          fullWidth
                          size="small"
                          style={{ paddingTop: 8, fontSize: '12px' }}
                        />
                        <CalendarTodayIcon
                          className="calendar-icon"
                          fontSize={'small'}
                        />
                      </div>
                    </DateRangePicker>
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={1}
                style={{
                  justifyContent: 'between',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  marginTop: '5px'
                }}
              >
                <Grid
                  item
                  xs={3}
                  width="60%"
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginLeft: 'auto',
                    marginTop: '8px',
                    display: 'flex'
                  }}
                >
                  <Button
                    sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                    variant="outlined"
                    onClick={resetFilterResult}
                    disabled={isClearDisabled}
                    className={isClearDisabled && 'clear-btn'}
                    style={{ height: '1.75rem', fontSize: '12px' }}
                    id="btn-resetFilterResult"
                  >
                    Clear
                  </Button>
                  <Button
                    sx={{ mt: { xs: 2, md: 0 } }}
                    id="btn-onSubmit"
                    variant="outlined"
                    type="submit"
                    disabled={isDisabled}
                    className={isDisabled && 'filter-btn'}
                    style={{ height: '1.75rem', fontSize: '12px' }}
                  >
                    Filter
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        )}
        <Divider />
        <TableContainer
          className="tableContainer"
          style={{ height: data?.length === 0 || !data ? '300px' : 'auto' }}
        >
          <Table
            style={{ borderCollapse: 'separate', position: 'relative' }}
            size="small"
          >
            <TableHead>
              <CustomTableRow style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableHeaderCell content="Client" smallFont />
                <TableHeaderCell content="Project" smallFont />
                <TableHeaderCell content="Resource Name" smallFont />
                <TableHeaderCell content="Summary By" smallFont />
                <TableHeaderCell
                  content="Duration (hh:mm)"
                  smallFont
                  isNotCapitalize
                />
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {data &&
                data?.length > 0 &&
                data.map((report, i) => {
                  return (
                    <TableRow hover key={i}>
                      <CustomTableCloumnCell content={report.clientName} />
                      <CustomTableCloumnCell content={report.projectName} />
                      <CustomTableCloumnCell content={report.employeeName} />
                      <CustomTableCloumnCell content={report.period} />
                      <CustomTableCloumnCell content={report.totalHours} />
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          {(data?.length === 0 || !data) && <NoRecord />}
        </TableContainer>
        <Box
          p={1}
          ref={pagination_wrapper_ref}
          sx={{ borderTop: '1px solid #eee' }}
        >
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={total ? total : 0}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={filter.offset}
            rowsPerPage={filter.limit}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
      </Card>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </div>
  );
});

export default SummarizedReportTable;
